import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../api/api";
import { jwtDecode } from "jwt-decode";

import TeacherPanel from "../panel/TeacherPanel";
import "../styles/TeacherResults.css";
import {
  FaClipboardList,
  FaBookOpen,
  FaTasks,
  FaMedal,
  FaPencilRuler,
  FaProjectDiagram,
  FaFileAlt,
  FaFlask,
  FaTrophy,
} from "react-icons/fa";

// Exams definitions
const exams = [
  { name: "Unit Test 1", icon: <FaClipboardList />, color: "sr-orange2" },
  { name: "Unit Test 2", icon: <FaClipboardList />, color: "sr-red2" },
  { name: "Half-Yearly Exam", icon: <FaBookOpen />, color: "sr-green2" },
  { name: "Pre-Board Exam", icon: <FaTasks />, color: "sr-pink2" },
  { name: "Annual Exam", icon: <FaMedal />, color: "sr-darkred2" },
  { name: "Class Test", icon: <FaPencilRuler />, color: "sr-orange2" },
  { name: "Assignment / Project", icon: <FaProjectDiagram />, color: "sr-green2" },
  { name: "Mid-Term Test", icon: <FaFileAlt />, color: "sr-blue2" },
  { name: "Practical Exam", icon: <FaFlask />, color: "sr-purple2" },
  { name: "Final Term Exam", icon: <FaTrophy />, color: "sr-deeporange2" },
];



const TeacherResults = () => {
  const { teacherId: urlTeacherId } = useParams();
  const navigate = useNavigate();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeExam, setActiveExam] = useState(null);
  const [selectedYear, setSelectedYear] = useState("");

  const [teacherAssignments, setTeacherAssignments] = useState([]);
  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [editableResults, setEditableResults] = useState([]);
  const [studentMarks, setStudentMarks] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [previousYearExamsData, setPreviousYearExamsData] = useState({});
  const token = localStorage.getItem("token");

  // Fetch years once on mount
useEffect(() => {
  if (!token) return;
  api.get("/api/teachers/available-years")
    .then((res) => {
      setAvailableYears(res.data);
      // build exam mapping dynamically
      const data = {};
      res.data.forEach((year) => {
        data[year] = exams.map((e) => ({
          ...e,
          name: `${e.name} ${year}`,
        }));
      });
      setPreviousYearExamsData(data);
    })
    .catch((err) => console.error("Error fetching years:", err));
}, [token]);

  // 🔹 Fetch student marks for previous year results
  useEffect(() => {
    if (!selectedStudent || !activeExam || !selectedYear) {
      setStudentMarks([]);
      return;
    }

    api.get("/api/teachers/get-results", {
        params: {
          studentId: Number(selectedStudent),
          examName: activeExam.name.replace(/\s\d{4}$/, ""),
          academicYear: selectedYear,
        },
      })
      .then((res) => {
        setStudentMarks(res.data);
        console.log("Marks fetched:", res.data);
      })
      .catch((err) => {
        console.error("Error fetching marks:", err);
      });
  }, [selectedStudent, activeExam, selectedYear, token]);

  // Decode token and validate teacher
  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    try {
      const decoded = jwtDecode(token);
      const tokenTeacherId = decoded.teacherId || decoded.id || decoded.userId;
      if (!tokenTeacherId) {
        alert("🚫 Invalid token");
        localStorage.removeItem("token");
        navigate("/");
        return;
      }
      if (String(tokenTeacherId) !== urlTeacherId) {
        alert("🚫 Unauthorized access!");
        navigate("/");
      }
    } catch (err) {
      console.error("Invalid token", err);
      localStorage.removeItem("token");
      navigate("/");
    }
  }, [token, urlTeacherId, navigate]);

  // Fetch assignments
  useEffect(() => {
    if (!token) return;

   api.get("/api/teachers/assignments")
      .then((res) => {
        const assignmentsArray = Array.isArray(res.data) ? res.data : res.data.data || [];
        setTeacherAssignments(assignmentsArray);
        setTeacherSubjects([...new Set(assignmentsArray.map((a) => a.subject_name))]);
      })
      .catch((err) => {
        console.error("Error fetching assignments:", err);
      });
  }, [token]);

  // Fetch students
  useEffect(() => {
    if (!token || !selectedClass || !selectedSection) {
      setStudents([]);
      setSelectedStudent("");
      return;
    }

    api.get("/api/teachers/students", {
        params: { classId: Number(selectedClass), sectionId: Number(selectedSection) },
      })
      .then((res) => {
        const studentsArray = Array.isArray(res.data) ? res.data : res.data.data || [];
        setStudents(studentsArray);
        setEditableResults(studentsArray.map((s) => ({ ...s })));
      })
      .catch((err) => console.error("Error fetching students:", err));
  }, [selectedClass, selectedSection, token]);

  // Handle mark change
  const handleMarkChange = (studentId, subject, value) => {
    setEditableResults((prev) =>
      prev.map((s) =>
        s.student_id === studentId ? { ...s, [subject]: Number(value) } : s
      )
    );
  };

  // Save results for current exams
  const handleSaveResult = async () => {
    if (!selectedStudent || !activeExam) return;

    const student = editableResults.find(
      (s) => s.student_id === Number(selectedStudent)
    );
    if (!student) return;

    try {
      const subjectsToSave = teacherAssignments
        .filter(
          (a) =>
            String(a.class_id) === selectedClass &&
            String(a.section_id) === selectedSection
        )
        .map((a) => a.subject_name);

      const examName = activeExam.name;

      for (const subject of subjectsToSave) {
  await api.post("/api/teachers/save-result", {
    studentId: student.student_id,
    subject,
    marks: student[subject] || 0,
    examName,
  });
}

      alert(`✅ Saved marks for ${student.student_name}`);
    } catch (err) {
      console.error(err);
      if (err.response?.data?.message) {
        alert(`❌ ${err.response.data.message}`);
      } else {
        alert("❌ Error saving marks");
      }
    }
  };

  const previousExams = selectedYear ? previousYearExamsData[selectedYear] : [];

  return (
    <>
      <TeacherPanel onToggle={setIsSidebarCollapsed} />

      <div className={`sr-main-content2 ${isSidebarCollapsed ? "collapsed" : ""}`}>
        {/* Current Exams */}
        <h3 className="sr-title2">📚 Current Exams</h3>
        <div className="sr-card-grid2">
          {exams.map((exam, idx) => (
            <div
              key={idx}
              className={`sr-card2 ${exam.color}`}
              onClick={() => {
                setActiveExam(exam);
                setSelectedClass("");
                setSelectedSection("");
                setSelectedStudent("");
              }}
            >
              <div className="sr-left2">
                <div className="sr-label2">{exam.name}</div>
                <div className="sr-count2">Update Results</div>
              </div>
              <div className="sr-icon-chip2">{exam.icon}</div>
            </div>
          ))}
        </div>

        {/* Previous Year Results */}
        <h3 className="sr-title2" style={{ marginTop: "30px" }}>
          📖 Previous Year Results
        </h3>
<select
  value={selectedYear}
  onChange={(e) => setSelectedYear(e.target.value)}
  className="year-select2"
>
  <option value="">Select Year</option>
  {availableYears.map((year) => (
    <option key={year} value={year}>
      {year}
    </option>
  ))}
</select>

        {selectedYear ? (
          <div className="sr-card-grid2">
            {previousExams.map((exam, idx) => (
              <div
                key={idx}
                className={`sr-card2 ${exam.color}`}
                onClick={() => setActiveExam(exam)}
              >
                <div className="sr-left2">
                  <div className="sr-label2">{exam.name}</div>
                  <div className="sr-count2">See Results</div>
                </div>
                <div className="sr-icon-chip2">{exam.icon}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="sr-placeholder-box2">
            Please select a year to see previous results
          </div>
        )}
      </div>

{/* Modal */}
{activeExam && (
  <div className="sr-modal-backdrop2" onClick={() => setActiveExam(null)}>
    <div className="sr-result-card2" onClick={(e) => e.stopPropagation()}>
      <h3>
        {activeExam.name}{" "}
        {selectedYear ? "- Previous Results" : "- Update Results"}
      </h3>

      {/* Dropdowns */}
      <div className="dropdown-row2">
        {/* Class Dropdown */}
        <select
          value={selectedClass}
          onChange={(e) => {
            setSelectedClass(e.target.value);
            setSelectedSection("");
            setSelectedStudent("");
          }}
        >
          <option value="">Select Class</option>
          {[...new Map(
            teacherAssignments.map((a) => [a.class_id, a.class_name])
          )].map(([id, name]) => (
            <option key={id} value={String(id)}>
              {name}
            </option>
          ))}
        </select>

        {/* Section Dropdown */}
        <select
          value={selectedSection}
          onChange={(e) => {
            setSelectedSection(e.target.value);
            setSelectedStudent("");
          }}
          disabled={!selectedClass}
        >
          <option value="">Select Section</option>
          {[...new Map(
            teacherAssignments
              .filter((a) => String(a.class_id) === selectedClass)
              .map((a) => [a.section_id, a.section_name])
          )].map(([id, name]) => (
            <option key={id} value={String(id)}>
              Section {name}
            </option>
          ))}
        </select>

        {/* Student Dropdown */}
        <select
          value={selectedStudent}
          onChange={(e) => setSelectedStudent(e.target.value)}
          disabled={!selectedSection}
        >
          <option value="">Select Student</option>
          {students.map((s) => (
            <option key={s.student_id} value={String(s.student_id)}>
              {s.student_name}
            </option>
          ))}
        </select>
      </div>

      {/* 🔹 Current Exam → Update only */}
      {!selectedYear && selectedStudent && (
        <>
          {[...new Set(
            teacherAssignments
              .filter(
                (a) =>
                  String(a.class_id) === selectedClass &&
                  String(a.section_id) === selectedSection
              )
              .map((a) => a.subject_name)
          )].map((subject, idx) => (
            <div className="maths-update-box2" key={`${subject}-${idx}`}>
              <label>{subject} Marks:</label>
              <input
                type="number"
                value={
                  editableResults.find(
                    (s) => String(s.student_id) === selectedStudent
                  )?.[subject] || ""
                }
                onChange={(e) =>
                  handleMarkChange(Number(selectedStudent), subject, e.target.value)
                }
              />
            </div>
          ))}

          <button
            className="save-btn2"
            disabled={!selectedStudent}
            onClick={handleSaveResult}
          >
            💾 Save Marks
          </button>
        </>
      )}

      {/* 🔹 Previous Year → View only */}
      {selectedYear && selectedStudent && (
        <div className="marks-box2">
          <h4>📊 {activeExam.name} Results</h4>
          {studentMarks.length > 0 ? (
            <ul>
              {studentMarks.map((m, idx) => (
                <li key={idx}>
                  {m.subject_name}: <strong>{m.marks}</strong>
                </li>
              ))}
            </ul>
          ) : (
            <p>No results found for this student.</p>
          )}
        </div>
      )}
    </div>
  </div>
)}

    </>
  );
};

export default TeacherResults;
